/* PP variant feedback — the buy button goes visibly busy the INSTANT a size or colour is picked.
 *
 * Why this exists (anh Huy, 08/08/2026): picking another variant re-renders the product section
 * through the Section Rendering API — a network round trip measured at ~1.0-1.1s on the dev
 * tunnel (faster on the live CDN, but never instant). Horizon leaves the OLD button fully live
 * for that whole window: a shopper who taps a sold-out size keeps looking at an active
 * ADD TO CART, and a tap in that window submits the PREVIOUS variant. This file closes that
 * window: the moment the selection changes, the button dims and stops taking activations.
 *
 * The busy state rides Shopify's own standard event, not a DOM guess. Every variant selection
 * dispatches `shopify:product:select` (standard-events.d.ts:678) carrying `event.promise`, which
 * settles exactly when the re-render data lands — the same promise sticky-add-to-cart.js and
 * product-card.js already build on.
 *
 * Three details are load-bearing, each one a confirmed audit finding (08/08/2026):
 *
 *   GENERATION VALUE, not a bare attribute. variant-picker.js aborts the previous fetch on every
 *   new selection and REJECTS the old promise — so the old
 *   selection's `.finally` fires while the new one is still in flight. Each busy mark stores its
 *   own generation number in the attribute; a settling promise only clears the mark it set.
 *   product-form.js guards its own submit path with the same counter idea.
 *
 *   ELEMENT-SCOPED clearing, never document-wide. The main PDP form and a quick-add modal (which
 *   carries its own add-to-cart-component) can both be mid-update at once; a global sweep would
 *   un-dim whichever one did NOT just settle.
 *
 *   A CAPTURE-PHASE CLICK BLOCKER, not just pointer-events. Keyboard activation (Enter/Space on
 *   the focused button) never consults pointer-events, but it does dispatch a click — one
 *   listener stops both input paths the same way. product-form.js also rejects submits while its
 *   variant request is pending, so this remains safe if another listener bypasses the UI guard.
 *
 * Loaded on EVERY page (see layout/theme.liquid): quick-add modals with variant pickers open from
 * product cards on the homepage, collections and search too — the same reasoning pp-card-hover.js
 * documents for itself. Progressive: without JS, nothing changes at all.
 */
(() => {
  'use strict';

  const BUSY_ATTR = 'data-pp-variant-updating';
  let generation = 0;

  /** @param {Element} atc @param {string} gen */
  const clearIfCurrent = (atc, gen) => {
    if (atc.getAttribute(BUSY_ATTR) === gen) {
      atc.removeAttribute(BUSY_ATTR);
      atc.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('shopify:product:select', (event) => {
    const origin = event.target instanceof Element ? event.target : null;
    /* Scope to where the selection happened — the section on a product page, the <dialog> for
       quick-add — so one surface can never dim or un-dim the other's button. No fallback to
       document on purpose: if a future picker ever dispatches from outside both containers,
       marking NOTHING fails safe, while a document-wide query could mark an unrelated button. */
    const scope = origin?.closest('.shopify-section, dialog');
    if (!scope) return;

    /* Resolve the input FIRST: the button to mark is the one nearest the control that fired —
       never "first add-to-cart-component in the section", which would grab card 1's button when
       a swatch on card N fired (audit find: latent until someone drops interactive swatch cards
       into one shared section). Falls back to the section's button when the event carries no
       option value id. */
    const detail = /** @type {{ detail?: { optionValueId?: string | number } }} */ (event).detail;
    const optionValueId = detail && detail.optionValueId != null ? String(detail.optionValueId) : null;
    const input = optionValueId
      ? scope.querySelector('input[data-option-value-id="' + CSS.escape(optionValueId) + '"]')
      : null;
    let atc = null;
    for (let holder = input; holder && holder !== scope.parentElement; holder = holder.parentElement) {
      atc = holder.querySelector('add-to-cart-component');
      if (atc) break;
    }
    if (!atc) atc = scope.querySelector('add-to-cart-component');
    if (!atc) return;

    generation += 1;
    const gen = String(generation);
    atc.setAttribute(BUSY_ATTR, gen);
    atc.setAttribute('aria-busy', 'true');

    /* The morph PATCHES this element in place (measured: attributes survive the update), so the
       reference stays live. If a future Horizon change swaps to full replacement, the clone
       arrives without the attribute and this clear becomes a harmless no-op on a detached node —
       correct either way. */
    const finish = () => {
      if (atc.getAttribute(BUSY_ATTR) !== gen) return; /* a newer selection owns the button now */
      clearIfCurrent(atc, gen);
    };
    const promise = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
    if (promise && typeof promise.then === 'function') {
      promise.then(
        () => {
          finish(); /* data landed — the morph right behind it owns the final truth */
        },
        () => {
          finish(); /* product-form.js keeps the failed selection inert */
        }
      );
    } else finish();
  });

  /* Blocks pointer AND keyboard activation while busy: keyboard Enter/Space on a button also
     dispatches a click, and capture phase runs before the form component's own handler. */
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const busyAtc = event.target.closest('add-to-cart-component[' + BUSY_ATTR + ']');
      if (busyAtc) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  );
})();
